/* eslint-disable no-underscore-dangle,arrow-parens,no-console */
import {
    KurentoRequestIds, KurentoResponse, KurentoResponseIds, VideoType,
} from '../SocketConnector';
import IceServers from './IceServers';
import MediaConstraints from './MediaConstraints';
import WebRTCStats, { IResultStats } from './WebRTCStats';

export default class WebRTCReceiver implements WebRTCReceiverType {
    private _onEvent?: onSendOfferType & onLocalIceCandidateType;
    private _onReconnect?: onReconnectType;
    private _onError?: onErrorType;
    private _onUpdateWebRTC?: onUpdateWebRTCType;
    private timeoutBeforeReconnect: number = MediaConstraints.timeout;
    private reconnectTimoutId?: number;
    private _stats?: WebRTCStats;
    presenterId?: string;
    video: IVideo = { candidatesQueue: [], connected: false };

    set onEvent(callback: onSendOfferType & onLocalIceCandidateType) {
        this._onEvent = callback;
    }

    set onError(callback: onErrorType) {
        this._onError = callback;
    }

    set onReconnect(callback: onReconnectType) {
        this._onReconnect = callback;
    }

    set onUpdateWebRTC(callback: onUpdateWebRTCType) {
        this._onUpdateWebRTC = callback;
    }

    start = async (presenterId: string) => {
        this.presenterId = presenterId;

        try {
            if (this.video.peerConnection) {
                this.stop();
            }

            const peerConnection = await this.startPeerConnection();

            this.setVideoProps({
                peerConnection,
            });
        } catch (e) {
            console.error('[Error] Ошибка инициализации стрима:', e);
            this.handleError(e, true);
        }
    };

    private startPeerConnection = async (): Promise<RTCPeerConnection> => {
        if (!this.presenterId) {
            throw Error('Отсутствует presenterId');
        }

        const peerConnection = this.initPeerConnection();

        peerConnection.onicecandidate = this.handleLocalIceCandidate;
        const description = await peerConnection.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true,
        });

        await peerConnection.setLocalDescription(description);
        await this.sendOffer(description, this.presenterId);
        return peerConnection;
    }

    private initPeerConnection = (options: RTCConfiguration = { iceServers: [IceServers.TurnServers] }): webkitRTCPeerConnection => new RTCPeerConnection(options);

    private handleLocalIceCandidate = async (event: RTCPeerConnectionIceEvent) => {
        if (event.candidate && this._onEvent) {
            this._onEvent({
                id: KurentoRequestIds.OnIceCandidate,
                candidate: event.candidate || undefined,
                videoType: VideoType.Cam,
                presenterId: this.presenterId,
            });
        } else if (event.candidate) {
            console.warn('Local ice candidate event has been ignored: ', event);
        }
    };

    private sendOffer = async (description: RTCSessionDescriptionInit, presenterId: string) => {
        if (this._onEvent) {
            this._onEvent({
                id: KurentoRequestIds.Start,
                sdpOffer: description.sdp,
                videoType: VideoType.Cam,
                presenterId,
            });
        }
    };

    private setVideoProps = <K extends keyof IVideo>(props: Pick<IVideo, K> | IVideo) => {
        this.video = {
            ...this.video,
            ...props,
        };
    };

    private handleRemoteIceCandidate = async (data: KurentoResponse) => {
        const { peerConnection } = this.video;
        if (!peerConnection) return;

        if (peerConnection.remoteDescription && data.candidate) {
            await peerConnection.addIceCandidate(data.candidate);
        } else if (data.candidate) {
            this.video.candidatesQueue.push(data.candidate);
        }
    };

    private handleResponseOffer = async (data: KurentoResponse) => {
        const { peerConnection, candidatesQueue } = this.video;
        if (peerConnection && data.sdpAnswer) {
            await peerConnection.setRemoteDescription(new RTCSessionDescription({
                sdp: data.sdpAnswer,
                type: 'answer',
            }));
            if (candidatesQueue.length) {
                await Promise.all(candidatesQueue.map(candidate => peerConnection.addIceCandidate(candidate)));

                candidatesQueue.splice(0);
            }
        }
    };

    handleMessage = async (data: KurentoResponse) => {
        switch (data.id) {
            case KurentoResponseIds.Response:
                await this.handleResponseOffer(data);
                break;
            case KurentoResponseIds.IceCandidate:
                await this.handleRemoteIceCandidate(data);
                break;
            case KurentoResponseIds.Error:
                console.error('[Error] Получена ошибка Куренто: ', data);
                await this.handleError(data);
                break;
            default:
                console.error('Unrecognized message: ', data);
        }
    };

    stop = () => {
        const { peerConnection } = this.video;

        if (peerConnection) {
            peerConnection.close();
        }

        delete this.video.remoteStream;

        if (this._stats) {
            this._stats.stopGetStats();
        }
    };

    subscribeToConnectEvent = (callback: () => void) => {
        const { peerConnection } = this.video;

        if (peerConnection) {
            peerConnection.oniceconnectionstatechange = () => {
                if (peerConnection.iceConnectionState === 'connected') {
                    const remoteMediaStream = new MediaStream();
                    peerConnection.getReceivers().forEach(r => remoteMediaStream.addTrack(r.track));
                    this.setVideoProps({
                        connected: true,
                        remoteStream: remoteMediaStream,
                        haveVideoStream: true,
                    });
                    this.startGetStats();
                    callback();
                }
            };

            peerConnection.onconnectionstatechange = () => {
                switch (peerConnection.connectionState) {
                    case 'failed':
                        console.error('[Error] WebRTC Соединение разорвано:', peerConnection);
                        this.setVideoProps({ connected: false });
                        this.sendToReconnect();
                        break;
                    case 'disconnected':
                        console.error('[Error] Соединение разорвано, WebRTC пытается восстановить соединение:', peerConnection);
                        break;
                    default:
                        console.log('Peer connection state changed: ', peerConnection.connectionState);
                }
            };
        }
    };

    private handleError = (error: KurentoResponse | Error, withoutReconnect: boolean = false) => {
        if (this._onError) {
            this._onError({
                videoType: VideoType.Cam,
                error,
                presenterId: this.presenterId,
            });
        }


        if (this.video.peerConnection) {
            delete this.video.peerConnection.onconnectionstatechange;
        }

        if (!withoutReconnect) {
            this.sendToReconnect();
        }
    }

    private sendToReconnect = () => {
        if (this.reconnectTimoutId === undefined && this._onReconnect && !this.video.haveVideoStream && !this.video.connected) {
            console.log(`[Reconnect] Попытка переподключения через: ${this.timeoutBeforeReconnect / 1000} секунд`);

            this.reconnectTimoutId = window.setTimeout(
              () => {
                  if (this._onReconnect) {
                      this._onReconnect({
                          videoType: VideoType.Cam,
                          presenterId: this.presenterId,
                      });
                  } else {
                      console.error('[Error] [Reconnect] Попытка переподключиться провалена. Нет функции для переподключения.');
                  }
                  delete this.reconnectTimoutId;
              },
              this.timeoutBeforeReconnect,
            );
            this.timeoutBeforeReconnect += MediaConstraints.timeout;
        }
    };

    private startGetStats = () => {
        const { peerConnection } = this.video;
        if (!peerConnection) return;
        const videoReceiver = peerConnection.getReceivers().find(r => r.track.kind === 'video');

        if (!videoReceiver) return;

        if (this._stats) {
            this._stats.stopGetStats();
        }

        this._stats = new WebRTCStats(videoReceiver);
        this._stats.startGetStats(this.handleStats);
    };

    private handleStats = (stats: IResultStats) => {
        const haveVideoStream = Boolean(stats.bytesPerSec && stats.framesDecodedPerSec && stats.packetsPerSec);
        if (this.video.haveVideoStream !== haveVideoStream) {
            this.setVideoProps({
                haveVideoStream,
            });

            if (this._onUpdateWebRTC) {
                this._onUpdateWebRTC();
            }
        }
    };
}


interface IVideo {
    remoteStream?: MediaStream;
    peerConnection?: RTCPeerConnection;
    candidatesQueue: RTCIceCandidate[];
    connected: boolean;
    haveVideoStream?: boolean;
}

type onReconnectType = (info: IConnectionProps) => void;
type onErrorType = (data: { error: KurentoResponse | Error } & IConnectionProps) => void;
type onUpdateWebRTCType = () => void;
type onSendOfferType = (data: { id: string; sdpOffer: string | undefined } & IConnectionProps) => void;
type onLocalIceCandidateType = (data: { id: string; candidate?: RTCIceCandidate } & IConnectionProps) => void;


interface IConnectionProps {
    presenterId?: string;
    videoType: VideoType;
}

export interface WebRTCReceiverType {
    start: (presenterId: string) => Promise<void>;
    stop: () => void;
    handleMessage: (data: KurentoResponse) => void;
    subscribeToConnectEvent: (callback: () => void) => void;
    presenterId?: string;
    video: IVideo;
    onEvent: onSendOfferType & onLocalIceCandidateType;
    onError: onErrorType;
    onReconnect: onReconnectType;
}
